import React, { useState } from 'react';
import Avatar from '@mui/material/Avatar';
import PersonIcon from '@mui/icons-material/Person';
import { blue } from '@mui/material/colors';
import {
  Box,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Divider,
  useTheme,
} from '@mui/material';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import useCredentials from 'hooks/useCredentials';

const UserDetail = () => {
  const { logout, userData } = useCredentials();
  const [open, setOpen] = useState(false);
  const theme = useTheme();

  const handleOpenModal = () => {
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
  };

  const handleConfirmLogout = () => {
    setOpen(false);
    logout();
  };

  const getUserInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map((word) => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getRoleDisplayName = (roles) => {
    if (!roles || roles.length === 0) return 'User';

    const roleMap = {
      admin: 'Administrator',
      user: 'User',
      TRAINER: 'Trainer',
      TRAINER_SUPER_ADMIN: 'Super Trainer',
      COURSE_REVIEWER: 'Course Reviewer',
      SFL_USER: 'SFL User',
      SFL_LEARN_ADMIN: 'SFL Admin',
    };

    return roleMap[roles[0]] || roles[0];
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '100%',
      }}
    >
      {/* User Info Section */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 1,
        }}
      >
        <Tooltip title="User Profile" arrow>
          <Avatar
            sx={{
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              width: 48,
              height: 48,
              fontSize: '1.1rem',
              fontWeight: 600,
              boxShadow: theme.shadows[2],
              '&:hover': {
                boxShadow: theme.shadows[4],
                transform: 'scale(1.05)',
                transition: 'all 0.2s ease-in-out',
              },
            }}
          >
            {getUserInitials(userData?.employeeName)}
          </Avatar>
        </Tooltip>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {userData?.employeeName || 'User'}
          </Typography>

          <Chip
            label={getRoleDisplayName(userData?.roles)}
            size="small"
            color="primary"
            variant="outlined"
            sx={{
              mt: 0.5,
              fontSize: '0.7rem',
              height: '20px',
              '& .MuiChip-label': {
                px: 1,
              },
            }}
          />
        </Box>

        {/* Logout Button */}
        <Tooltip title="Sign Out" arrow>
          <IconButton
            size="small"
            onClick={handleOpenModal}
            sx={{
              color: theme.palette.error.main,
              '&:hover': {
                backgroundColor: theme.palette.error.main + '10',
                transform: 'scale(1.05)',
                transition: 'all 0.2s ease-in-out',
              },
            }}
          >
            <ExitToAppIcon sx={{ fontSize: '1.1rem' }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Confirmation Modal */}
      <Dialog
        open={open}
        onClose={handleCloseModal}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 320,
          },
        }}
      >
        <DialogTitle
          id="logout-dialog-title"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: theme.palette.error.main,
          }}
        >
          <ExitToAppIcon />
          Confirm Sign Out
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="logout-dialog-description"
            sx={{ color: theme.palette.text.secondary }}
          >
            Are you sure you want to sign out?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button onClick={handleCloseModal} variant="outlined" sx={{ borderRadius: 1 }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmLogout}
            variant="contained"
            color="error"
            sx={{ borderRadius: 1 }}
            autoFocus
          >
            Sign Out
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserDetail;
